import { useCallback, useState, useRef, useEffect } from 'react';
import useUploadChimeMutation from '../../hooks/useUploadChimeMutation';
import { AlertType } from '../../../design-system/DSAlert';
import useAppContext from '../../../hooks/useAppContext';

const MAX_CHIME_DURATION_SECONDS = 40;

type UseChimeUploadProps = {
  initialChimeUrl?: string;
  initialChimeName?: string;
  initialChimesDuration?: number;
  showAlert: (alert: { message: string; alertType: AlertType }) => void;
  onUploadSuccess?: (url: string) => void;
  onRemove?: () => void;
};

type UseChimeUploadReturn = {
  chimeUrl: string;
  chimeName: string;
  chimesDurationInSeconds: number | undefined;
  isLoadingChime: boolean;
  handleChimeFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleRemoveChime: () => void;
};

const useChimeUpload = ({
  initialChimeUrl = '',
  initialChimeName = '',
  initialChimesDuration,
  showAlert,
  onUploadSuccess,
  onRemove,
}: UseChimeUploadProps): UseChimeUploadReturn => {
  const { isAdmin } = useAppContext();
  const [chimeUrl, setChimeUrl] = useState<string>(initialChimeUrl);
  const [chimeName, setChimeName] = useState<string>(initialChimeName);
  const [chimesDurationInSeconds, setChimesDurationInSeconds] = useState<
    number | undefined
  >(initialChimesDuration);

  const chimeAbortControllerRef = useRef<AbortController | null>(null);
  const { mutate: uploadChimeMutation, isPending: isLoadingChime } =
    useUploadChimeMutation();

  useEffect(() => {
    return () => {
      if (chimeAbortControllerRef.current) {
        chimeAbortControllerRef.current.abort();
      }
    };
  }, []);

  const handleChimeFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const validTypes = ['audio/mp3', 'audio/wave', 'audio/wav', 'audio/mpeg'];

      if (!validTypes.includes(file.type)) {
        showAlert({
          message: 'Invalid chime file type. Please upload MP3 or WAV files.',
          alertType: AlertType.ERROR,
        });
        event.target.value = '';
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        chimeAbortControllerRef.current = new AbortController();
        uploadChimeMutation(
          {
            file: reader.result as string,
            fileName: file.name,
            fileType: file.type,
          },
          {
            onSuccess: (response) => {
              if (response.status === 'approved' && response.url) {
                if (
                  response.duration &&
                  response.duration > MAX_CHIME_DURATION_SECONDS &&
                  !isAdmin
                ) {
                  showAlert({
                    message: `Chime duration must be less than ${MAX_CHIME_DURATION_SECONDS} seconds. Your file is ${response.duration} seconds.`,
                    alertType: AlertType.ERROR,
                  });
                  event.target.value = '';
                  return;
                }
                setChimeUrl(response.url);
                setChimeName(file.name);
                setChimesDurationInSeconds(response.duration);
                onUploadSuccess?.(response.url);
              } else {
                showAlert({
                  message: response.error || 'Failed to upload chime',
                  alertType: AlertType.ERROR,
                });
                event.target.value = '';
              }
            },
            onError: () => {
              showAlert({
                message: 'Failed to upload chime',
                alertType: AlertType.ERROR,
              });
              event.target.value = '';
            },
          },
        );
      };
      reader.readAsDataURL(file);
    },
    [showAlert, uploadChimeMutation],
  );

  const handleRemoveChime = useCallback(() => {
    if (chimeAbortControllerRef.current) {
      chimeAbortControllerRef.current.abort();
    }
    setChimeUrl('');
    setChimeName('');
    setChimesDurationInSeconds(undefined);
    onRemove?.();
  }, [onRemove]);

  return {
    chimeUrl,
    chimeName,
    chimesDurationInSeconds,
    isLoadingChime,
    handleChimeFileChange,
    handleRemoveChime,
  };
};

export default useChimeUpload;
